<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import UserAvatar from '@/components/UserAvatar.vue';
import RoleBadge from '@/components/RoleBadge.vue';
import ProfileGifts from '@/components/ProfileGifts.vue';
import ProfileWall from '@/components/ProfileWall.vue';
import type { MemberProfile, VirtualGift } from '@/types/api';
import { fetchMember, fetchMemberGifts, toggleAvatarLike } from '@/lib/apiClient';
import { useAuth } from '@/composables/useAuth';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const { token, user } = useAuth();

const loading = ref(true);
const liking = ref(false);
const member = ref<MemberProfile | null>(null);
const gifts = ref<VirtualGift[]>([]);

const userId = Number(route.params.id);

onMounted(async () => {
  if (!token.value || !userId) {
    loading.value = false;
    return;
  }

  try {
    const [memberResponse, giftsResponse] = await Promise.all([
      fetchMember(token.value, userId),
      fetchMemberGifts(token.value, userId),
    ]);
    member.value = memberResponse.member;
    gifts.value = giftsResponse.gifts;
  } catch {
    member.value = null;
    gifts.value = [];
  } finally {
    loading.value = false;
  }
});

async function likeAvatar() {
  if (!token.value || !member.value || member.value.id === user.value?.id) return;
  liking.value = true;

  try {
    const result = await toggleAvatarLike(token.value, member.value.id);
    member.value = {
      ...member.value,
      likedByMe: result.liked,
      avatarLikesCount: result.avatarLikesCount,
    };
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error instanceof Error ? error.message : 'Не удалось поставить лайк',
      life: 3000,
    });
  } finally {
    liking.value = false;
  }
}

function sendGift() {
  if (!member.value) return;
  router.push({ name: 'gifts', query: { to: String(member.value.id) } });
}
</script>

<template>
  <div class="page">
    <Button label="Назад" icon="pi pi-arrow-left" severity="secondary" text @click="router.back()" />

    <ProgressSpinner v-if="loading" />

    <template v-else-if="member">
      <Card class="profile-card">
        <template #content>
          <div class="profile">
            <div class="profile__avatar-wrap">
              <UserAvatar :nickname="member.nickname" :avatar="member.avatar" size="xlarge" />
              <Button
                v-if="member.id !== user?.id"
                :icon="member.likedByMe ? 'pi pi-heart-fill' : 'pi pi-heart'"
                :label="`${member.avatarLikesCount}`"
                class="profile__like-btn"
                :loading="liking"
                @click="likeAvatar"
              />
              <span v-else-if="member.avatarLikesCount > 0" class="profile__likes">
                ❤️ {{ member.avatarLikesCount }}
              </span>
            </div>

            <div class="profile__info">
              <h1>{{ member.nickname }}</h1>
              <RoleBadge :label="member.roleLabel" :color="member.roleBadgeColor" />
              <p class="profile__about">
                {{ member.about || 'Человек молчит. Уважаем.' }}
              </p>
              <small class="profile__since">
                Залетел с {{ new Date(member.createdAt).toLocaleDateString('ru-RU') }}
              </small>
              <Button
                v-if="member.id !== user?.id"
                label="Подарить подарок"
                icon="pi pi-gift"
                class="profile__gift-btn"
                @click="sendGift"
              />
            </div>
          </div>
        </template>
      </Card>

      <Card>
        <template #content>
          <ProfileGifts :gifts="gifts" />
        </template>
      </Card>

      <ProfileWall :wall-user-id="member.id" :wall-owner-nickname="member.nickname" />
    </template>

    <Card v-else>
      <template #content>
        <p>Пользователь не найден.</p>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 820px;
}

.profile-card :deep(.p-card-body) {
  background: linear-gradient(135deg, #fff1f2, #fdf2f8);
}

.profile {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.profile__avatar-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.profile__like-btn :deep(.p-button) {
  background: linear-gradient(135deg, #fb7185, #f472b6);
  border: none;
}

.profile__likes {
  color: #db2777;
  font-weight: 700;
}

.profile__info {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.profile__info h1 {
  margin: 0;
  color: #831843;
}

.profile__about {
  margin: 0.5rem 0 0;
  color: #9d174d;
  line-height: 1.6;
}

.profile__since {
  color: #be185d;
}

.profile__gift-btn {
  margin-top: 0.5rem;
  align-self: flex-start;
}

@media (max-width: 640px) {
  .profile {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .profile__info {
    align-items: center;
  }

  .profile__gift-btn {
    align-self: center;
  }
}
</style>
